// Reusable widget-grid layout state with userSettings persistence (extracted
// from performance-v2.tsx, #4932 Phase 0.4).
//
// The layout lives in a caller-supplied Jotai atom (in-memory, per app
// convention — no localStorage) so it survives in-session navigation, is
// hydrated once from userSettings.widget_layouts, and is written back
// debounced (1s) after any reorder/resize. Returns plain accessors —
// never components.
import { useCallback, useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import type { PrimitiveAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';

import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import { userSettingsQueryOptions, useUpdateUserSettingsMutation } from '@/lib/query-options';

export function useWidgetLayout(
  layoutAtom: PrimitiveAtom<Record<string, WidgetConfig[]>>,
  defaults: Record<string, WidgetConfig[]>,
): {
  getTabLayout: (tabId: string) => WidgetConfig[];
  handleReorder: (tabId: string, layout: WidgetConfig[]) => void;
  handleResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
} {
  const [allLayouts, setAllLayouts] = useAtom(layoutAtom);
  const { data: userSettings } = useQuery(userSettingsQueryOptions());
  const settingsMutation = useUpdateUserSettingsMutation();
  const layoutInitialized = useRef(false);
  const skipNextSave = useRef(false);

  // Initialize layouts from persisted settings
  useEffect(() => {
    if (userSettings && !layoutInitialized.current) {
      if (userSettings.widget_layouts) {
        skipNextSave.current = true;
        setAllLayouts(userSettings.widget_layouts as Record<string, WidgetConfig[]>);
      }
      layoutInitialized.current = true;
    }
  }, [userSettings, setAllLayouts]);

  // Debounced save of layout changes (1s)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!layoutInitialized.current) return;
    if (skipNextSave.current) { skipNextSave.current = false; return; }

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      if (Object.keys(allLayouts).length > 0) {
        settingsMutation.mutate({ widget_layouts: allLayouts });
      }
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [allLayouts]);

  const getTabLayout = useCallback(
    (tabId: string) => allLayouts[tabId] || defaults[tabId] || [],
    [allLayouts],
  );

  const handleReorder = useCallback((tabId: string, layout: WidgetConfig[]) => {
    setAllLayouts(prev => ({ ...prev, [tabId]: layout }));
  }, [setAllLayouts]);

  const handleResize = useCallback((tabId: string, id: string, width: 33 | 50 | 66 | 100) => {
    setAllLayouts(prev => {
      const current = prev[tabId] || defaults[tabId] || [];
      return {
        ...prev,
        [tabId]: current.map(w => w.id === id ? { ...w, width } : w),
      };
    });
  }, [setAllLayouts]);

  return { getTabLayout, handleReorder, handleResize };
}
